<script lang="ts">
	import { resolve } from '$app/paths';
	import { page } from '$app/state';
	import ArrowIcon from '$lib/components/marketing/redesign2025/ArrowIcon.svelte';
	import * as Sheet from '$lib/components/ui/sheet/index.js';
	import { m } from '$lib/paraglide/messages.js';
	import { marketingCanonicalPath } from '$lib/utils/marketing-routes';
	import { getLocale, localizeHref } from '@/paraglide/runtime';
	import { currentUser } from '@/utils/ApiClient';
	import Plus from '@lucide/svelte/icons/plus';
	import MarketingAccountMenu from './MarketingAccountMenu.svelte';
	import MarketingLocalePicker from './MarketingLocalePicker.svelte';

	const locale = $derived(getLocale());

	const canonicalPath = $derived(marketingCanonicalPath(page.url.pathname));

	const navLinks = $derived.by(() => {
		void locale;
		return [
			{
				href: localizeHref('/barcos'),
				label: m.nav_boats(),
				desc: m.nav_m_boats_desc(),
				match: '/barcos'
			},
			{
				href: localizeHref('/alquiler-lista-septima'),
				label: m.nav_m_owners(),
				desc: m.nav_m_owners_desc(),
				match: '/alquiler-lista-septima'
			},
			{
				href: localizeHref('/zarpar-experiencias'),
				label: m.nav_m_exp(),
				desc: m.nav_m_exp_desc(),
				match: '/zarpar-experiencias'
			},
			{
				href: localizeHref('/alquiler-lista-sexta'),
				label: m.nav_m_charter(),
				desc: m.nav_m_charter_desc(),
				match: '/alquiler-lista-sexta'
			},
			{
				href: localizeHref('/registro-capitan'),
				label: m.nav_captain_short(),
				desc: m.nav_m_captain_desc(),
				match: '/registro-capitan'
			}
		];
	});

	const actionLinks = $derived.by(() => {
		void locale;
		return [
			{
				href: localizeHref('/registro'),
				label: m.ideal_just_ibex_thrive(),
				desc: m.nav_m_create_desc()
			},
			{
				href: localizeHref('/anadir-barco'),
				label: m.zesty_spry_gibbon_hint(),
				desc: m.nav_m_add_desc()
			},
			{
				href: localizeHref('/registro-capitan'),
				label: m.footer_link_captain(),
				desc: m.nav_m_captain_action_desc()
			}
		] as const;
	});

	let open = $state(false);
	let actionsOpen = $state(false);
	let accountSheetOpen = $state(false);

	function closeMenu() {
		open = false;
	}

	function closeActions() {
		actionsOpen = false;
	}

	function openMenu() {
		actionsOpen = false;
		open = true;
	}

	function openActions() {
		open = false;
		actionsOpen = true;
	}

	$effect(() => {
		if (open || actionsOpen || accountSheetOpen) {
			document.documentElement.style.overflow = 'hidden';
		} else {
			document.documentElement.style.overflow = '';
		}
		return () => {
			document.documentElement.style.overflow = '';
		};
	});
</script>

<svelte:window
	onkeydown={(e) => {
		if (e.key === 'Escape') {
			closeMenu();
			closeActions();
			accountSheetOpen = false;
		}
	}}
/>

<!-- Floating bar -->
<div
	class="fixed right-3.5 bottom-[max(0.875rem,env(safe-area-inset-bottom))] left-3.5 z-60 grid grid-cols-[1fr_auto_1fr] items-center rounded-[15px] border border-zp-ink/10 bg-zp-paper/82 py-2.5 pr-2.5 pl-[18px] shadow-[0_20px_44px_-20px_rgba(27,24,64,0.55)] backdrop-blur-[18px] backdrop-saturate-150 md:hidden"
>
	<button
		type="button"
		onclick={openMenu}
		class="inline-flex items-center gap-3 justify-self-start px-1 py-2 text-[0.94rem] font-semibold tracking-[0.01em] text-zp-ink"
		aria-label={m.nav_open_menu()}
	>
		<span class="flex w-[18px] flex-col items-start gap-1">
			<span class="block h-[1.5px] w-[18px] rounded-sm bg-zp-ink"></span>
			<span class="block h-[1.5px] w-[11px] rounded-sm bg-zp-ink"></span>
		</span>
		{m.nav_menu()}
	</button>
	<a
		href={resolve(localizeHref('/barcos'))}
		data-sveltekit-reload
		class="justify-self-center px-3 py-2 text-[0.94rem] font-semibold tracking-[0.01em] {canonicalPath ===
		'/barcos'
			? 'text-zp-accent'
			: 'text-zp-ink'}"
	>
		{m.nav_boats()}
	</a>
	{#if $currentUser}
		<MarketingAccountMenu mode="sheet-trigger" bind:sheetOpen={accountSheetOpen} />
	{:else}
		<button
			type="button"
			onclick={openActions}
			aria-label={m.nav_quick_actions_aria()}
			aria-expanded={actionsOpen}
			aria-haspopup="dialog"
			class="grid size-11 shrink-0 place-items-center justify-self-end rounded-[9px] bg-zp-navy text-zp-paper"
		>
			<Plus class="size-5" strokeWidth={2.25} />
		</button>
	{/if}
</div>

<!-- Quick actions sheet -->
{#if !$currentUser}
	<Sheet.Root bind:open={actionsOpen}>
		<Sheet.Content
			side="bottom"
			showCloseButton={false}
			class="nav-sheet z-75 gap-0 rounded-t-[18px] border-zp-line bg-zp-paper px-5 pt-3 pb-[calc(1.25rem+env(safe-area-inset-bottom))] shadow-[0_-20px_44px_-20px_rgba(27,24,64,0.35)] md:hidden"
		>
			<Sheet.Header class="sr-only">
				<Sheet.Title>{m.nav_sheet_actions_title()}</Sheet.Title>
				<Sheet.Description>{m.nav_sheet_actions_desc()}</Sheet.Description>
			</Sheet.Header>
			<div class="mx-auto mb-4 h-1 w-10 rounded-full bg-zp-line" aria-hidden="true"></div>
			<nav class="flex flex-col">
				{#each actionLinks as link (link.href)}
					<a
						href={resolve(link.href)}
						onclick={closeActions}
						class="flex min-h-11 items-center justify-between gap-3 border-b border-zp-line py-3.5 first:border-t"
					>
						<span class="flex flex-col gap-0.5">
							<span class="font-serif text-[1.35rem] leading-tight tracking-[-0.015em] text-zp-ink">
								{link.label}
							</span>
							<span class="text-[0.82rem] text-zp-muted">{link.desc}</span>
						</span>
						<span class="shrink-0 text-[1rem] text-zp-accent">→</span>
					</a>
				{/each}
			</nav>
			<button
				type="button"
				onclick={closeActions}
				class="mt-4 w-full rounded-[9px] border border-zp-line py-3 text-[0.86rem] font-semibold text-zp-ink"
			>
				{m.nav_close()}
			</button>
		</Sheet.Content>
	</Sheet.Root>
{/if}

<!-- Editorial menu sheet -->
<div
	class="nav-sheet nav-menu-overlay fixed inset-0 z-70 flex min-h-0 flex-col bg-zp-paper px-5 pt-4 pb-[calc(18px+env(safe-area-inset-bottom))] md:hidden {open
		? 'nav-menu-overlay--open'
		: 'nav-menu-overlay--closed'}"
	aria-hidden={!open}
>
	<div class="mb-1.5 flex shrink-0 items-center justify-between">
		<enhanced:img
			src="/static/img/landing/redesign-2025/logo-navy.png?w=320"
			alt="Zarpar"
			sizes="145px"
			class="h-5 w-auto"
		/>
		<button
			type="button"
			onclick={closeMenu}
			class="grid size-[42px] place-items-center rounded-full border border-zp-line text-[15px] leading-none text-zp-ink"
			aria-label={m.nav_close_menu()}
		>
			✕
		</button>
	</div>

	<nav class="min-h-0 flex-1 overflow-y-auto overscroll-contain">
		<div class="flex flex-col">
			{#each navLinks as link (link.label)}
				<a
					href={resolve(link.href)}
					onclick={closeMenu}
					class="flex items-baseline justify-between gap-2.5 border-b border-zp-line py-3 first:border-t"
				>
					<span class="flex min-w-0 flex-col gap-1">
						<span
							class="font-serif text-[clamp(1.5rem,7.8vw,1.95rem)] leading-[0.96] tracking-[-0.015em] {link.match ===
							canonicalPath
								? 'text-zp-navy italic'
								: 'text-zp-ink'}"
						>
							{link.label}
						</span>
						<span class="text-[0.8rem] leading-snug text-zp-muted">{link.desc}</span>
					</span>
					<span class="shrink-0 self-center text-[1rem] text-zp-accent">→</span>
				</a>
			{/each}
		</div>
	</nav>

	<MarketingLocalePicker layout="menu" onSelect={closeMenu} class="shrink-0" />

	<div class="flex shrink-0 flex-col gap-2.5 pt-3">
		{#if $currentUser}
			<a
				href={resolve(localizeHref('/cuenta'))}
				onclick={closeMenu}
				class="inline-flex w-full items-center justify-center gap-2 rounded-[9px] bg-zp-navy px-4 py-3 text-[0.86rem] font-semibold tracking-[0.01em] text-zp-paper"
			>
				{m.nav_cta_my_account()}
				<ArrowIcon />
			</a>
			<div class="flex items-center justify-center text-[0.92rem] text-zp-muted">
				<a href="https://zarpar.eu/ayuda" class="font-semibold text-zp-ink"
					>{m.patchy_round_cuckoo_stop()}</a
				>
			</div>
		{:else}
			<a
				href={resolve(localizeHref('/registro'))}
				onclick={closeMenu}
				class="inline-flex w-full items-center justify-center gap-2 rounded-[9px] bg-zp-navy px-4 py-3 text-[0.86rem] font-semibold tracking-[0.01em] text-zp-paper"
			>
				{m.ideal_just_ibex_thrive()}
				<ArrowIcon />
			</a>
			<div class="flex items-center justify-between gap-3 text-[0.92rem] text-zp-muted">
				<a href="https://zarpar.eu/ayuda" class="font-semibold text-zp-ink"
					>{m.patchy_round_cuckoo_stop()}</a
				>
				<a
					href={resolve(localizeHref('/iniciar-sesion'))}
					onclick={closeMenu}
					class="font-semibold text-zp-ink">{m.new_upper_thrush_flow()}</a
				>
			</div>
		{/if}
	</div>
</div>

<style>
	:global(.nav-sheet) {
		font-family:
			'Geist',
			system-ui,
			-apple-system,
			sans-serif;
		font-size: 17px;
		line-height: 1.6;
		color: var(--color-zp-ink);
		-webkit-font-smoothing: antialiased;
		text-rendering: optimizeLegibility;
	}

	:global(.nav-sheet a) {
		text-decoration: none;
	}

	:global(.nav-sheet .font-serif) {
		font-family: 'Newsreader', Georgia, 'Times New Roman', serif;
		font-weight: 400;
		letter-spacing: -0.018em;
	}

	.nav-menu-overlay {
		transition:
			translate 460ms cubic-bezier(0.16, 0.84, 0.28, 1),
			opacity 460ms cubic-bezier(0.16, 0.84, 0.28, 1);
	}

	.nav-menu-overlay--open {
		translate: 0 0;
		opacity: 1;
	}

	.nav-menu-overlay--closed {
		pointer-events: none;
		translate: 0 100%;
		opacity: 0;
	}
</style>
